import { useCallback, useMemo } from 'react';
import { useSolutionOrg } from '../../config/useSolutionOrg';
import { InvoiceSimpleSolutionOrgName, OPTIMUS_EVENT_FOR_EXTERNAL_LINK } from '../constants';
import { useOptimizelyFlag } from '../hooks/useOptimizelyFlag';
import { EngagementNavigationExternalLinksFlag } from '../OptimizelyFlagsConstants';
import { normalizeExternalUrl } from './urlUtils';

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        [key: string]: {
          postMessage: (message: string) => void;
        };
      };
    };
    android?: {
      postMessage: (message: string) => void;
    };
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

// eslint-disable-next-line no-shadow
export enum NavigationTarget{
  Webview = 'webview',
  Browser = 'browser'
}

export const useNavigationUtils = () => {
  const { isNativeAppExperience, solutionOrgName } = useSolutionOrg();
  const isInvoiceSimple = useMemo(() => (
    solutionOrgName === InvoiceSimpleSolutionOrgName
  ), [solutionOrgName]);

  const {
    decision: externalLinkDecision,
    isDecisionReady: isExternalLinkDecisionReady,
  } = useOptimizelyFlag({
    flag: EngagementNavigationExternalLinksFlag,
  });

  const openExternalLink = useCallback((url: string, navigationTarget: NavigationTarget =
  NavigationTarget.Browser) => {
    if (!url) return;

    const normalizedUrl = normalizeExternalUrl(url);
    if (!normalizedUrl) return;

    // optimizely flag off behavior
    if (!isExternalLinkDecisionReady || !externalLinkDecision?.enabled) {
      if (navigationTarget === NavigationTarget.Webview) {
        window.open(normalizedUrl, '_parent');
      } else {
        window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // Invoice Simple special-case (open in same view)
    // TODO: have invoice simple implement
    // navigationTarget feature
    if (navigationTarget === NavigationTarget.Webview && isInvoiceSimple
    ) {
      window.open(normalizedUrl, '_parent');
      return;
    }

    const messagePayload = { type: 'openExternal', url: normalizedUrl, target: navigationTarget };
    const messagePayloadStr = JSON.stringify(messagePayload);

    // Iframe experience
    if (isNativeAppExperience) {
      // send message to script to communicate with native app
      window.parent.postMessage({
        type: OPTIMUS_EVENT_FOR_EXTERNAL_LINK,
        payload: messagePayload,
      }, '*');
      return;
    }

    // Outside iframe but inside joist or invoice simple app
    if (window?.webkit?.messageHandlers?.optimusEvent?.postMessage) {
      // Joist iOS app WebView (WKWebView) compatibility
      window.webkit.messageHandlers.optimusEvent.postMessage(messagePayloadStr);
      return;
    }
    if (window?.android?.postMessage) {
      // Joist android app webView
      window.android.postMessage(messagePayloadStr);
      return;
    }
    if (window?.ReactNativeWebView?.postMessage) {
      // React Native IOS or Android WebView (invoice simple)
      window.ReactNativeWebView.postMessage(messagePayloadStr);
      return;
    }

    // Normal browser/desktop experience
    if (navigationTarget === NavigationTarget.Webview) {
      window.open(normalizedUrl, '_parent');
    } else {
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
    }
  }, [isNativeAppExperience,
    isExternalLinkDecisionReady,
    externalLinkDecision?.enabled,
    isInvoiceSimple,
  ]);

  // For URLs that need an async request (e.g. an SSO token), call this directly
  // from the click handler so desktop browsers can open the tab before awaiting.
  const openExternalLinkAsync = useCallback(async (buildUrl: () => Promise<string>) => {
    const usesNativeBridge = isExternalLinkDecisionReady && externalLinkDecision?.enabled && (
      isNativeAppExperience ||
      window.webkit?.messageHandlers?.optimusEvent?.postMessage ||
      window.android?.postMessage ||
      window.ReactNativeWebView?.postMessage
    );
    if (usesNativeBridge) {
      openExternalLink(await buildUrl());
      return;
    }

    // Passing noopener/noreferrer to window.open returns null even on success.
    // Keep a handle for navigation, then sever the opener before loading anything.
    const tab = window.open('about:blank', '_blank');
    if (!tab) throw new Error('Unable to open a browser tab');
    try {
      tab.opener = null;
      const url = normalizeExternalUrl(await buildUrl());
      if (!url || tab.closed) throw new Error('Unable to navigate the browser tab');
      // Navigate within the reserved tab and keep the existing noreferrer policy.
      const link = tab.document.createElement('a');
      link.href = url;
      link.rel = 'noreferrer';
      link.target = '_self';
      tab.document.body.appendChild(link);
      link.click();
    } catch (error) {
      tab.close();
      throw error;
    }
  }, [isExternalLinkDecisionReady, externalLinkDecision?.enabled,
    isNativeAppExperience, openExternalLink]);

  return { openExternalLink, openExternalLinkAsync };
};
